import React, { useMemo, useState } from 'react';
import { Layout, Avatar, Dropdown, Button } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../redux/root-reducer';
import { HeaderProps } from '../../types/types';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import Logo from '../Logo';
import ThemeToggle from './ThemeToggle';
import './style.scss';

const { Header: AntHeader } = Layout;

const resolvePageTitleKey = (pathname: string): string => {
  if (pathname === '/' || pathname.startsWith('/dashboard')) return 'navigation.dashboard';
  if (pathname.startsWith('/customers')) return 'navigation.customers';
  if (pathname.startsWith('/products')) return 'navigation.products';
  if (pathname.startsWith('/sales-representatives')) return 'navigation.salesRepresentatives';
  if (pathname.startsWith('/orders')) return 'navigation.orders';
  if (pathname.startsWith('/visits')) return 'navigation.visits';
  if (pathname.startsWith('/reports')) return 'navigation.reports';
  if (pathname.startsWith('/loyalty-points')) return 'navigation.loyaltyPoints';
  if (pathname.startsWith('/offers')) return 'navigation.offers';
  if (pathname.startsWith('/payment-methods')) return 'navigation.paymentMethods';
  if (pathname.startsWith('/customer-types')) return 'navigation.customerTypes';
  if (pathname.startsWith('/customer-classifications')) return 'navigation.customerClassifications';
  if (pathname.startsWith('/users')) return 'navigation.users';
  if (pathname.startsWith('/models')) return 'navigation.models';
  if (pathname.startsWith('/roles')) return 'navigation.roles';
  if (pathname.startsWith('/permissions')) return 'navigation.permissions';
  if (pathname.startsWith('/settings')) return 'navigation.settings';
  return 'navigation.dashboard';
};

const Header: React.FC<HeaderProps> = ({
  onLogout,
  onMenuToggle,
  sidebarCollapsed = false,
  isMobileViewport = false,
}) => {
  const { userInfo } = useSelector((state: RootState) => state.setReducer);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isRTL = i18n.language === 'ar' || i18n.language.startsWith('ar-');

  const pageTitle = useMemo(() => {
    const key = resolvePageTitleKey(location.pathname);
    return t(key);
  }, [location.pathname, t]);

  const toggleIcon = isMobileViewport ? (
    <MenuOutlined />
  ) : sidebarCollapsed ? (
    <MenuUnfoldOutlined />
  ) : (
    <MenuFoldOutlined />
  );

  return (
    <AntHeader className={`dashboard-header${isRTL ? ' dashboard-header--rtl' : ''}`}>
      <div className="dashboard-header__inner">
        <div className="header-left">
          <Button
            type="text"
            icon={toggleIcon}
            onClick={onMenuToggle}
            className={`sidebar-toggle-btn${!isMobileViewport ? ' sidebar-toggle-btn--desktop' : ''}${!isMobileViewport && sidebarCollapsed ? ' sidebar-toggle-btn--rail' : ''}`}
            aria-label={t('navigation.toggleMenu', 'Toggle navigation')}
            aria-expanded={!sidebarCollapsed}
          />

          <div className="header-context">
            {isMobileViewport && (
              <div className="header-context__logo">
                <Logo size="small" invert style={{ width: 34, height: 'auto' }} />
              </div>
            )}
            <div className="header-context__text">
              {!isMobileViewport && (
                <span className="header-context__eyebrow">
                  {t('branding.retailManagement', 'Retail Admin')}
                </span>
              )}
              <h1 className="header-context__title">{pageTitle}</h1>
            </div>
          </div>
        </div>

        <div className="header-right">
          <ThemeToggle />

          <div className="header-lang">
            <LanguageSwitcher type="button" />
          </div>

          <Dropdown
            menu={{
              items: [
                {
                  key: 'profile',
                  icon: <UserOutlined />,
                  label: t('navigation.profile'),
                },
                {
                  key: 'settings',
                  icon: <SettingOutlined />,
                  label: t('navigation.settings'),
                },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: t('navigation.logout'),
                  danger: true,
                  className: 'logout-item',
                },
              ],
              onClick: ({ key }) => {
                if (key === 'logout') {
                  onLogout();
                }
                setDropdownOpen(false);
              },
            }}
            trigger={['click']}
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            placement="bottomRight"
            overlayClassName="user-dropdown-menu"
          >
            <div className="user-section" role="button" tabIndex={0}>
              <Avatar size={36} icon={<UserOutlined />} />
              <span className="user-name">{userInfo?.user?.name || t('common.user', 'User')}</span>
              <DownOutlined className="dropdown-arrow" />
            </div>
          </Dropdown>
        </div>
      </div>
    </AntHeader>
  );
};

export default Header;
